import { Box, Button, Group, Pagination, SegmentedControl, Table, Title } from "@mantine/core";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "urql";

import Loading from "../Loading";
import { processingStatusLabel, useProcessing } from "../utils";
import DeleteButton from "./DeleteButton";
import { type TtsProjectPageQuery, type TtsProjectPageQueryVariables, ttsProjectPageQuery } from "./queries";
import classes from "./TtsProjectsPage.module.css";

function TtsProjectsPage(): JSX.Element {
    const [search, setSearch] = useSearchParams();
    const page = useMemo(() => Number(search.get("page") || 1), [search]);
    const filter = (search.get("filter") as TtsProjectPageQueryVariables["filter"]) || "MINE";

    const [result, execute] = useQuery<TtsProjectPageQuery, TtsProjectPageQueryVariables>({
        query: ttsProjectPageQuery,
        variables: {
            page,
            filter,
        },
        requestPolicy: "cache-and-network",
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: set defaults once
    useEffect(() => {
        if (!search.get("filter")) {
            setSearch((prev) => {
                const s = new URLSearchParams(prev);
                s.set("filter", filter);
                s.set("page", String(page));
                return s;
            });
        }
    }, []);

    useProcessing(result, execute, (r) => {
        const items = r.data?.ttsProjectPage.items ?? [];
        return items.some((item) => item.processingStatus === "PROCESSING");
    });

    const refreshPage = () => {
        execute({ requestPolicy: "network-only" });
    };

    let main = null;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else if (!result.data || result.data.ttsProjectPage.maxPage === 0) {
        main = <Box>No TTS projects have been created</Box>;
    } else {
        main = (
            <>
                <Pagination
                    total={result.data.ttsProjectPage.maxPage}
                    value={page}
                    onChange={(p) => setSearch({ filter, page: String(p) })}
                />
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Created</Table.Th>
                            <Table.Th className={classes.actionHeader}>Actions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {result.data.ttsProjectPage.items.map((project) => (
                            <Table.Tr key={project.id}>
                                <Table.Td>{processingStatusLabel(project.processingStatus)}</Table.Td>
                                <Table.Td>{project.name}</Table.Td>
                                <Table.Td>{new Date(project.created).toLocaleString("en-GB")}</Table.Td>
                                <Table.Td className={classes.actionCell}>
                                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                                        <Button component={Link} to={`${project.id}/`}>
                                            Details
                                        </Button>
                                        <DeleteButton project={project} onDeleted={refreshPage} />
                                    </Group>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
                <Pagination
                    total={result.data.ttsProjectPage.maxPage}
                    value={page}
                    onChange={(p) => setSearch({ filter, page: String(p) })}
                />
            </>
        );
    }

    return (
        <Box className={classes.grid}>
            <Group justify="space-between" align="center">
                <Title order={2}>Text to Speech</Title>
                <SegmentedControl
                    value={filter}
                    onChange={(f) => setSearch({ filter: f, page: "1" })}
                    data={[
                        { label: "Mine", value: "MINE" },
                        { label: "Others", value: "OTHERS" },
                        { label: "All", value: "ALL" },
                    ]}
                />
            </Group>
            <Group justify="space-between" align="center">
                <Button component={Link} to="add">
                    Create new
                </Button>
            </Group>
            {main}
        </Box>
    );
}

export default TtsProjectsPage;
